import React from "react";
import * as Sentry from "@sentry/react";

/**
 * Le filet : ce qui empeche un dossier de faire disparaitre le back-office.
 *
 * Nicolas, 18/09 : « je le retrouve bien dans la colonne a gauche mais
 * impossible de l'ouvrir, ca me met une page blanche quand je clic dessus »,
 * puis « visiblement ce dernier dossier a casse quelque chose ».
 *
 * ─────────────────────────────────────────────────────────────────────────
 * POURQUOI UNE PAGE BLANCHE, ET PAS UN MESSAGE
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Depuis React 16, une erreur levee pendant le rendu DEMONTE tout l'arbre si
 * personne ne l'attrape. Le back-office n'avait aucune frontiere d'erreur :
 * la moindre faute sur un seul dossier — un champ qu'un dossier Mode direct
 * ne porte pas, une date qu'une purge a laissee vide — vidait `#root`. D'ou
 * l'ecran blanc, et d'ou la suite : plus rien ne repond, pas meme les boutons
 * d'une autre page, puisqu'il n'y a plus d'application.
 *
 * Ce composant ne corrige aucune faute. Il fait deux choses, et elles valent
 * mieux que le blanc :
 *   1. il garde le reste de l'ecran vivant — la liste des dossiers, le menu,
 *      la navigation : on peut ouvrir le dossier suivant ;
 *   2. il DIT ce qui s'est passe, avec le message de l'erreur. Un retour qui
 *      porte « Cannot read properties of undefined (reading 'name') » se
 *      corrige dans l'heure ; un retour qui dit « page blanche » se cherche
 *      une journee.
 *
 * L'erreur part aussi a Sentry, qui est deja monte dans cette application.
 */
export default class Filet extends React.Component {
  constructor(props) {
    super(props);
    this.state = { erreur: null };
  }

  static getDerivedStateFromError(erreur) {
    return { erreur };
  }

  componentDidCatch(erreur, infos) {
    // La console d'abord : c'est la qu'on regarde quand on a le navigateur
    // sous la main.
    console.error("[filet] rendu interrompu :", erreur, infos?.componentStack);
    try {
      Sentry.captureException(erreur, { extra: { zone: this.props.zone, infos } });
    } catch {
      // Sentry non configure en developpement : l'absence de rapport ne doit
      // pas devenir une deuxieme erreur.
    }
  }

  /**
   * Un autre dossier choisi dans la liste : le filet se remet en place de
   * lui-meme. Sans cela, le message du dossier precedent restait affiche et
   * il fallait cliquer « Reessayer » avant de pouvoir ouvrir le suivant
   * (Nicolas, 04/10 : « il faut recharger »).
   */
  componentDidUpdate(precedent) {
    if (this.state.erreur && precedent.cle !== this.props.cle) {
      this.setState({ erreur: null });
    }
  }

  /**
   * Repartir sans recharger la page.
   *
   * Reessayer suffit quand la faute venait de la donnee affichee : on choisit
   * un autre dossier, et le filet se remet en place.
   */
  reessayer = () => {
    this.setState({ erreur: null });
    this.props.onReessayer?.();
  };

  render() {
    const { erreur } = this.state;
    if (!erreur) return this.props.children;

    return (
      <div
        style={{
          margin: 16,
          padding: 16,
          borderRadius: 12,
          background: "#fff7ed",
          border: "1px solid #fdba74",
          color: "#7c2d12",
          fontSize: 14,
          lineHeight: 1.5,
        }}
      >
        <div style={{ fontWeight: 800, marginBottom: 6 }}>
          {this.props.titre || "Cet affichage n'a pas pu s'ouvrir"}
        </div>
        <div style={{ marginBottom: 10 }}>
          Le reste du back-office fonctionne : vous pouvez ouvrir un autre
          dossier, ou changer de page.
        </div>
        <div
          style={{
            fontFamily: "monospace",
            fontSize: 12,
            background: "#fff",
            border: "1px solid #fed7aa",
            borderRadius: 8,
            padding: "6px 8px",
            marginBottom: 10,
            overflowWrap: "anywhere",
          }}
        >
          {String(erreur?.message || erreur)}
        </div>
        <button
          type="button"
          onClick={this.reessayer}
          style={{
            padding: "6px 12px",
            borderRadius: 8,
            border: "1px solid #fdba74",
            background: "#fff",
            color: "#7c2d12",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Réessayer
        </button>
      </div>
    );
  }
}
